import { Component, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  AppearanceService,
  AppearanceSettings,
  CornerRadiusScale,
  DEFAULT_APPEARANCE,
  DensityScale,
  FONT_FAMILIES,
  FONT_SIZE_PX,
  FontFamilyKey,
  FontSizeScale,
  FontWeightScale,
  THEME_PRESETS,
  ThemePresetKey,
} from '../../../services/appearance.service';

const FONT_SIZE_ORDER: FontSizeScale[] = ['small', 'default', 'large', 'xlarge'];

const FONT_SIZE_LABEL: Record<FontSizeScale, string> = {
  small: 'Small',
  default: 'Default',
  large: 'Large',
  xlarge: 'X-Large',
};

@Component({
  selector: 'app-appearance',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './appearance.component.html',
  styleUrl: './appearance.component.css',
})
export class AppearanceComponent implements OnDestroy {
  private appearanceService = inject(AppearanceService);

  draft: AppearanceSettings = this.appearanceService.current;

  readonly fontFamilies = FONT_FAMILIES;
  readonly defaultFontFamily = DEFAULT_APPEARANCE.fontFamily;

  /** Two per row, matching the design's 3×2 theme grid. */
  readonly themeRows = [
    THEME_PRESETS.slice(0, 2),
    THEME_PRESETS.slice(2, 4),
    THEME_PRESETS.slice(4, 6),
  ];

  constructor() {
    // Load every candidate family up front so the picker samples render
    // in their own typeface immediately.
    this.fontFamilies.forEach((family) => this.appearanceService.ensureFontLoaded(family));
  }

  readonly fontWeightOptions: { value: FontWeightScale; label: string }[] = [
    { value: 'light', label: 'Light' },
    { value: 'default', label: 'Regular' },
    { value: 'bold', label: 'Bold' },
  ];

  readonly densityOptions: { value: DensityScale; label: string; icon: string }[] = [
    { value: 'compact', label: 'Compact', icon: 'bx bx-align-justify' },
    { value: 'default', label: 'Default', icon: 'bx bx-align-middle' },
    { value: 'spacious', label: 'Roomy', icon: 'bx bx-align-left' },
  ];

  readonly radiusOptions: { value: CornerRadiusScale; label: string }[] = [
    { value: 'sharp', label: 'Sharp' },
    { value: 'default', label: 'Default' },
    { value: 'round', label: 'Round' },
  ];

  readonly fontSizeTicks = FONT_SIZE_ORDER.map((value) => ({
    value,
    label: `${FONT_SIZE_LABEL[value]} (${FONT_SIZE_PX[value]}px)`,
  }));

  ngOnDestroy() {
    // Leaving the page discards any unsaved preview.
    this.appearanceService.revertPreview();
  }

  get fontSizeIndex(): number {
    return FONT_SIZE_ORDER.indexOf(this.draft.fontSize);
  }

  set fontSizeIndex(index: number) {
    this.draft.fontSize = FONT_SIZE_ORDER[index] || 'default';
    this.previewDraft();
  }

  get fontSizeValueLabel(): string {
    return `${FONT_SIZE_LABEL[this.draft.fontSize]} (${FONT_SIZE_PX[this.draft.fontSize]}px)`;
  }

  get sliderFillPercent(): number {
    return (this.fontSizeIndex / (FONT_SIZE_ORDER.length - 1)) * 100;
  }

  setFontFamily(value: FontFamilyKey) {
    this.draft.fontFamily = value;
    this.previewDraft();
  }

  setFontWeight(value: FontWeightScale) {
    this.draft.fontWeight = value;
    this.previewDraft();
  }

  setTheme(value: ThemePresetKey) {
    this.draft.theme = value;
    this.previewDraft();
  }

  setCornerRadius(value: CornerRadiusScale) {
    this.draft.cornerRadius = value;
    this.previewDraft();
  }

  setDensity(value: DensityScale) {
    this.draft.density = value;
    this.previewDraft();
  }

  stepRowHeight(delta: number) {
    this.draft.tableRowHeight = Math.min(72, Math.max(32, this.draft.tableRowHeight + delta * 4));
    this.previewDraft();
  }

  stepCellPadding(delta: number) {
    this.draft.cellPaddingX = Math.min(32, Math.max(4, this.draft.cellPaddingX + delta * 2));
    this.previewDraft();
  }

  save() {
    this.appearanceService.save(this.draft);
    this.draft = this.appearanceService.current;
  }

  reset() {
    this.appearanceService.reset();
    this.draft = this.appearanceService.current;
  }

  get isDefault(): boolean {
    return JSON.stringify(this.draft) === JSON.stringify(DEFAULT_APPEARANCE);
  }

  get isDirty(): boolean {
    return JSON.stringify(this.draft) !== JSON.stringify(this.appearanceService.current);
  }

  private previewDraft() {
    this.appearanceService.preview(this.draft);
  }
}
